import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { Wallet, MagnifyingGlass, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import UpgradeGate from "@/components/UpgradeGate";
import { hasPlan } from "@/lib/planAccess";

export default function Salary() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");

  const [role, setRole] = useState(
    user?.profile?.desired_career || ""
  );
  const [country, setCountry] = useState(
    user?.profile?.country || ""
  );
  const [years, setYears] = useState("");
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    // Use the user's profile only as an initial fallback. The saved Salary
    // Estimator state returned by /salary/latest always takes precedence.
    if (user?.profile) {
      setRole((current) => current || user.profile.desired_career || "");
      setCountry((current) => current || user.profile.country || "");
    }

    api.get("/salary/latest")
      .then(({ data }) => {
        if (data.result) {
          setResult({ ...data.result, cached: true });
          setGeneratedAt(data.generated_at);
          if (data.inputs) {
            if (data.inputs.role) setRole(data.inputs.role);
            if (data.inputs.country) setCountry(data.inputs.country);
            if (data.inputs.experience_years !== undefined && data.inputs.experience_years !== null) {
              setYears(String(data.inputs.experience_years));
            }
            if (data.inputs.industry !== undefined) setIndustry(data.inputs.industry);
          }
        }
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/salary/estimate", {
        role,
        country,
        experience_years: parseFloat(years) || 0,
        industry,
      });
      setResult(data);
      setGeneratedAt(data.generated_at || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estimate failed");
    } finally {
      setLoading(false);
    }
  };

  const currency = result?.currency || "USD";
  const currencySymbol = result?.currency_symbol || "";

  if (!paid) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="salary-page">
        <Navbar />
        <DashSidebar />
        <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-3xl mx-auto lg:mx-0">
          <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Earnings Intel</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium mb-2">Salary Estimator</h1>
          <UpgradeGate title="Unlock Salary Intelligence" description="Salary Estimator is included with Pro and Premium, with up to 50 searches or refreshes each month." />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="salary-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-3xl mx-auto lg:mx-0">
        <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">
          // Earnings Intel
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium mb-2">
          Salary Estimator
        </h1>
        <p className="text-zinc-500 mb-6 sm:mb-8 text-sm sm:text-base">
          Get a calibrated salary range with the assumptions spelled out.
        </p>

        <form
          onSubmit={submit}
          className="bg-[#0F0F12] border border-white/10 rounded-xl p-4 sm:p-6 grid md:grid-cols-2 gap-4"
        >
          <div className="md:col-span-2">
            <label className="text-xs tracking-widest uppercase text-zinc-400">
              Role
            </label>
            <input
              required
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Electrical Engineer"
              data-testid="salary-role"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none"
            />
          </div>

          <div>
            <label className="text-xs tracking-widest uppercase text-zinc-400">
              Country
            </label>
            <input
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. India / UAE / Saudi Arabia"
              data-testid="salary-country"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none"
            />
          </div>

          <div>
            <label className="text-xs tracking-widest uppercase text-zinc-400">
              Years of experience
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              data-testid="salary-years"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs tracking-widest uppercase text-zinc-400">
              Industry (optional)
            </label>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g. Construction / Oil & Gas / SaaS"
              data-testid="salary-industry"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="salary-submit"
            className="md:col-span-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2"
          >
            <MagnifyingGlass size={16} />
            {loading ? "Estimating..." : "Estimate salary"}
          </button>
        </form>

        {result && (
          <div className="mt-8" data-testid="salary-result">
            {generatedAt && (
              <div className="text-xs text-zinc-500 mb-4">
                {result.cached ? "✓ Loaded saved salary estimate" : "✓ Salary estimate generated"} · Saved on {new Date(generatedAt).toLocaleDateString()}
              </div>
            )}
            {result.level && (
              <div className="inline-flex items-center gap-2 text-xs bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1.5 mb-4">
                <Wallet size={12} className="text-blue-400" />
                <span className="text-blue-200">
                  {result.level} band · {currency}
                </span>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              {[
                { k: "low", label: "Low" },
                { k: "mid", label: "Mid" },
                { k: "high", label: "High" },
              ].map((c, i) => (
                <div
                  key={c.k}
                  className="bg-[#0F0F12] border border-white/10 rounded-xl p-3 sm:p-6 text-center"
                >
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {c.label}
                  </div>
                  <div
                    className={`text-xl sm:text-3xl font-medium font-mono mt-2 break-all ${
                      i === 0
                        ? "text-zinc-300"
                        : i === 1
                          ? "text-blue-400"
                          : "text-emerald-400"
                    }`}
                  >
                    {currencySymbol}
                    {(result[c.k] || 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {!!(result.assumptions || []).length && (
              <div
                className="mt-6 bg-[#0F0F12] border border-white/10 rounded-xl p-5"
                data-testid="salary-assumptions"
              >
                <div className="flex items-center gap-2 text-blue-400 mb-3">
                  <Info size={14} weight="duotone" />
                  <h3 className="text-sm font-medium">
                    Assumptions behind the estimate
                  </h3>
                </div>
                <ul className="space-y-2">
                  {result.assumptions.map((a) => (
                    <li
                      key={a}
                      className="text-sm text-zinc-300 flex gap-2"
                    >
                      <span className="text-blue-400">·</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.notes && (
              <div
                className="mt-4 text-sm text-zinc-400 bg-white/[0.02] border border-white/5 rounded-lg p-4"
                data-testid="salary-notes"
              >
                {result.notes}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
